import React, { useCallback, useEffect, useMemo, useRef } from "react";
import type { DefaultSectionT, SectionListData } from "react-native";
import { Platform, View } from "react-native";

import SectionList from "$src/core/components/atoms/SectionList/SectionList";
import BackToTopButton from "$src/core/components/molecules/BackToTopButton/BackToTopButton";
import type { GridRef } from "$src/core/components/organisms/Grid/types";
import GridSection from "$src/core/components/organisms/GridSection/GridSection";
import type { HeroRef } from "$src/core/components/organisms/Hero/Hero";
import Hero from "$src/core/components/organisms/Hero/Hero";
import LaneSection from "$src/core/components/organisms/LaneSection/LaneSection";
import type { SwimmingLaneRef } from "$src/core/components/organisms/SwimmingLane/types";
import useTabBarHeight from "$src/core/hooks/use-tab-bar-height";
import useTranslation from "$src/core/hooks/use-translation";
import type { AnyString, SectionDto } from "$src/core/models/dto";
import ErrorPage from "$src/core/pages/ErrorPage/ErrorPage";
import LoadingPage from "$src/core/pages/LoadingPage/LoadingPage";
import usePageQuery from "$src/core/queries/use-page-query";
import useStorefrontQuery from "$src/core/queries/use-storefront-query";
import Logging from "$src/core/services/logging/logging";
import $pageInsets from "$src/core/styles/page";
import CustomPlatform from "$src/core/utils/platform/platform";

import $, { FOCUS_SCROLL_INSET, IMAGE_DIMENSIONS } from "./DynamicPage.styles";

type Props = {
  slug?: "storefront" | AnyString;
  HeroAccessory?: React.ReactNode;
};

// Android TV holds no focus on mount. Claim it for the session's first page only.
const NEEDS_ENTRY_FOCUS = CustomPlatform.select({
  androidTV: true,
  default: false,
});
let hasClaimedEntryFocus = false;

const DynamicPage = ({ slug, HeroAccessory }: Props) => {
  const { t } = useTranslation();
  const tabBarHeight = useTabBarHeight();

  const heroRef = useRef<HeroRef | null>(null);
  const firstSectionRef = useRef<GridRef | SwimmingLaneRef | null>(null);

  const isStorefront = slug === "storefront";

  const storefrontQuery = useStorefrontQuery({
    enabled: isStorefront,
  });
  const pageQuery = usePageQuery(
    { slug: slug ?? "" },
    { isEnabled: slug && !isStorefront },
  );

  const { data, status, error, refetch } = isStorefront
    ? storefrontQuery
    : pageQuery;

  const renderItem = useCallback(
    ({ item, index }: { item: SectionDto; index: number }) => {
      const sectionRef = index === 0 ? firstSectionRef : undefined;
      if (item.type === "grid") {
        return <GridSection item={item} ref={sectionRef} />;
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (item.type === "lane") {
        return <LaneSection item={item} ref={sectionRef} />;
      }
      Logging.warn("Unknown section type", item);
      return null;
    },
    [],
  );

  const hero = data?.hero;

  const sections = useMemo<
    SectionListData<SectionDto, DefaultSectionT>[]
  >(() => {
    if (!data) return [];

    if (!data.sections || data.sections.length === 0) {
      return [
        {
          data: data.sections ?? [],
        },
      ];
    }

    return data.sections.map((section) => ({ data: [section] }));
  }, [data]);

  const handleBackToTop = useCallback(() => {
    if (heroRef.current) {
      heroRef.current.focus();
    } else {
      firstSectionRef.current?.requestFocusOnIndex(0);
    }
  }, [heroRef, firstSectionRef]);

  /* Give Android TV a focused item to start from, once the page has content to focus. */
  useEffect(() => {
    if (!NEEDS_ENTRY_FOCUS || hasClaimedEntryFocus || status !== "success") {
      return;
    }
    hasClaimedEntryFocus = true;
    handleBackToTop();
  }, [handleBackToTop, status]);

  if (status === "pending") {
    return <LoadingPage />;
  }

  if (status === "error") {
    return (
      <ErrorPage
        title={t("ERROR/DYNAMIC_PAGE/TITLE")}
        subtitle={t("ERROR/DYNAMIC_PAGE/SUBTITLE")}
        ctaText={t("COMMON/RETRY")}
        ctaIcon="retry"
        onPressCta={refetch}
        error={error}
      />
    );
  }

  return (
    <SectionList
      accessibilityRole="list"
      sections={sections}
      renderItem={renderItem}
      keyExtractor={(item) => `${item.type}-${item.id}`}
      contentContainerStyle={$.contentContainer({
        tabBarHeight,
        hasHero: !!hero,
      })}
      showsVerticalScrollIndicator={false}
      fadingEdgeLength={FOCUS_SCROLL_INSET}
      ListHeaderComponent={
        hero && (
          <View style={$.hero}>
            {HeroAccessory}
            <Hero
              ref={heroRef}
              contentContainerStyle={$pageInsets.horizontal}
              type={hero.type}
              id={hero.id}
              title={hero.title}
              description={hero.shortDescription}
              image={hero.image ?? hero.images}
              imageDimensions={IMAGE_DIMENSIONS.hero}
            />
          </View>
        )
      }
      ListFooterComponent={
        Platform.isTV ? <BackToTopButton onPress={handleBackToTop} /> : null
      }
    />
  );
};

export default DynamicPage;
